// src/contexts/UnsavedChangesContext.jsx
// Schutz vor Datenverlust beim Verlassen eines Editors: Ein Editor meldet sich
// mit registerGuard an, solange er ungespeicherte Aenderungen hat. Jede
// Navigation, die vorher durch requestNavigation laeuft (Hauptnavigation,
// Subtabs, Zurueck-Button im Kostentool), fragt dann in einem Dialog nach, ob
// zuerst gespeichert werden soll.
//
// Fenster schliessen/neu laden kann technisch nur der Browser selbst abfragen —
// das passiert ueber beforeunload in useEstimateEditor.
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

// Fallback, damit Komponenten auch ohne Provider funktionieren (Navigation
// laeuft dann einfach ungebremst durch).
const FALLBACK = {
  registerGuard: () => () => {},
  requestNavigation: (proceed) => {
    proceed();
    return true;
  },
};

const UnsavedChangesContext = createContext(FALLBACK);

export const useUnsavedChanges = () => useContext(UnsavedChangesContext);

// Navigation, die vorher nachfragt — als Ersatz fuer useNavigate dort, wo nur
// navigiert wird (ohne weitere Seiteneffekte).
export const useGuardedNavigate = () => {
  const navigate = useNavigate();
  const { requestNavigation } = useUnsavedChanges();
  return useCallback(
    (...args) => requestNavigation(() => navigate(...args)),
    [navigate, requestNavigation]
  );
};

export const UnsavedChangesProvider = ({ children }) => {
  // { isDirty: () => boolean, save: () => Promise<boolean>, label?: string }
  const guardRef = useRef(null);
  const [dialog, setDialog] = useState(null);

  const registerGuard = useCallback((guard) => {
    guardRef.current = guard;
    return () => {
      if (guardRef.current === guard) guardRef.current = null;
    };
  }, []);

  // Fuehrt proceed sofort aus, wenn nichts offen ist; sonst erst nach dem
  // Dialog. Rueckgabe: true = ohne Nachfrage ausgefuehrt.
  const requestNavigation = useCallback((proceed) => {
    const guard = guardRef.current;
    if (!guard?.isDirty?.()) {
      proceed();
      return true;
    }
    setDialog({ proceed, label: guard.label, saving: false, error: null });
    return false;
  }, []);

  const closeDialog = useCallback(() => setDialog(null), []);

  const leaveWithoutSaving = useCallback(() => {
    const proceed = dialog?.proceed;
    guardRef.current = null; // Editor wird verlassen, Guard nicht mehr gueltig
    setDialog(null);
    proceed?.();
  }, [dialog]);

  const saveAndLeave = useCallback(async () => {
    const guard = guardRef.current;
    const proceed = dialog?.proceed;
    setDialog((prev) => (prev ? { ...prev, saving: true, error: null } : prev));

    const ok = await (guard?.save ? guard.save() : Promise.resolve(true));
    if (!ok) {
      setDialog((prev) =>
        prev
          ? {
              ...prev,
              saving: false,
              error:
                "Speichern fehlgeschlagen. Bitte erneut versuchen oder ohne Speichern verlassen.",
            }
          : prev
      );
      return;
    }

    guardRef.current = null;
    setDialog(null);
    proceed?.();
  }, [dialog]);

  const value = useMemo(
    () => ({ registerGuard, requestNavigation }),
    [registerGuard, requestNavigation]
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      {dialog && (
        <div
          className="modal-overlay unsaved-changes-overlay"
          onClick={dialog.saving ? undefined : closeDialog}
        >
          <div
            className="modal-content estimate-modal unsaved-changes-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Ungespeicherte Änderungen</h3>
              {!dialog.saving && (
                <button className="modal-close" onClick={closeDialog}>
                  &times;
                </button>
              )}
            </div>
            <div className="modal-body">
              {dialog.error && <div className="error-message">{dialog.error}</div>}
              <p className="unsaved-changes-text">
                {dialog.label
                  ? `„${dialog.label}“ enthält ungespeicherte Änderungen.`
                  : "Es sind ungespeicherte Änderungen vorhanden."}{" "}
                Möchten Sie zuerst speichern?
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={closeDialog}
                disabled={dialog.saving}
              >
                Abbrechen
              </button>
              <button
                className="btn btn-secondary"
                onClick={leaveWithoutSaving}
                disabled={dialog.saving}
              >
                Ohne Speichern verlassen
              </button>
              <button
                className="btn btn-primary"
                onClick={saveAndLeave}
                disabled={dialog.saving}
                autoFocus
              >
                {dialog.saving ? "Speichern..." : "Speichern und verlassen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </UnsavedChangesContext.Provider>
  );
};

export default UnsavedChangesContext;
